import React from 'react';
import ProductTour from './ProductTour';
import { X } from 'lucide-react';

/**
 * Standalone /tour route — bare-bones page that renders just the product tour
 * on a dark backdrop. Handy for sharing direct links, embedding in emails,
 * pitch decks, Product Hunt hero, etc.
 */
export default function StandaloneTour() {
  const variant = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('variant');
      return ['storyboard', 'scrollytelling', 'tabbed', 'carousel'].includes(p) ? p : 'tabbed';
    } catch { return 'tabbed'; }
  })();

  return (
    <div className="min-h-screen" style={{ background: '#0F0A1F' }} data-testid="standalone-tour">
      {/* Minimal top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-lg border-b border-white/5" style={{ background: 'rgba(15, 10, 31, 0.85)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <a href="/preview" className="flex items-center gap-2.5 text-white hover:opacity-80 transition-opacity" data-testid="tour-brand-back">
            <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
              <path d="M15 3C15 3 6 13 6 20a9 9 0 0018 0c0-7-9-17-9-17z" fill="#A78BFA" />
            </svg>
            <span className="text-base font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>LodgiFlow</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 ml-2 hidden sm:inline">The Product Tour</span>
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/signup"
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold px-4 py-2 rounded-full transition-colors bg-white text-[#0F0A1F] hover:bg-white/90"
              data-testid="tour-cta-signup"
            >
              Start Free
            </a>
            <a
              href="/preview"
              aria-label="Close tour"
              className="h-9 w-9 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="tour-close"
            >
              <X className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      {/* Tour section — override its default background via CSS so it inherits the dark theme */}
      <div className="tour-standalone">
        <ProductTour variant={variant} />
      </div>

      {/* Footer nudge */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] uppercase tracking-widest text-white/40">
          <div>See the whole story at <a href="/preview" className="text-white/70 hover:text-white underline underline-offset-2" data-testid="tour-footer-preview">lodgiflow.com/preview</a></div>
          <div>© LodgiFlow · Enterprise hospitality tech, mapped.</div>
        </div>
      </footer>

      {/* Dark-mode overrides for the embedded tour section */}
      <style>{`
        .tour-standalone [data-testid="tour-tabbed"] {
          background: linear-gradient(180deg, #0F0A1F 0%, #1a0f3a 50%, #0F0A1F 100%) !important;
        }
        .tour-standalone [data-testid="tour-tabbed"] h2,
        .tour-standalone [data-testid="tour-tabbed"] h3 { color: #ffffff; }
        .tour-standalone [data-testid="tour-tabbed"] p,
        .tour-standalone [data-testid="tour-tabbed"] li { color: rgba(255,255,255,0.72); }
        .tour-standalone [data-testid="tour-keyboard-hint"] {
          background: rgba(255,255,255,0.05) !important;
          border-color: rgba(255,255,255,0.1) !important;
          color: rgba(255,255,255,0.55) !important;
        }
        .tour-standalone [data-testid="tour-keyboard-hint"] kbd {
          background: rgba(255,255,255,0.06) !important;
          border-color: rgba(255,255,255,0.15) !important;
          color: rgba(255,255,255,0.85) !important;
          box-shadow: 0 1px 0 rgba(255,255,255,0.05) !important;
        }
        /* Chapter cards — inactive */
        .tour-standalone [data-testid^="tabbed-tab-"] {
          background: rgba(255,255,255,0.02);
          border-color: rgba(255,255,255,0.06);
        }
        .tour-standalone [data-testid^="tabbed-tab-"]:hover {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.12);
        }
        /* Active chapter card */
        .tour-standalone [data-testid^="tabbed-tab-"].bg-white {
          background: rgba(167, 139, 250, 0.12) !important;
          border-color: rgba(167, 139, 250, 0.35) !important;
          box-shadow: 0 8px 30px -8px rgba(167, 139, 250, 0.3) !important;
        }
      `}</style>
    </div>
  );
}
